// /api/geo/* — map configuration, address search, and the time zone under a pin.
//
// The address provider is never called from the browser: its key lives on the
// server, which also caches and rate-limits. Every function here DEGRADES
// rather than throws — a failed lookup is an empty list or a null, and the
// forms fall back to typed address fields. Adding a building must never become
// impossible because a third party is down.
import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import { publicApi } from './public-client';
import { roundCoordinate } from '../function-utils/device-location';

export interface GeoConfig {
  /** null when the map is switched off; the forms then show no map at all. */
  map: { style_url: string; attribution: string } | null;
  /** null when no address provider is configured: no search, no reverse lookup. */
  geocoding: { attribution: string; attribution_url: string } | null;
}

// One place, in the shape the building form stores it.
export interface GeoPlace {
  place_id: string | null;
  /** Which provider issued place_id; null when there is no id. */
  place_provider: string | null;
  label: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
  country_code: string | null;
  latitude: number;
  longitude: number;
  formatted_address: string | null;
  kind: string | null;
}

export interface ZoneAtPoint {
  /** The IANA zone at the point, or null when there is no single answer. */
  timezone: string | null;
  /** Open water, or a border with more than one zone: the form asks. */
  ambiguous: boolean;
  candidates: string[];
}

const NO_CONFIG: GeoConfig = { map: null, geocoding: null };

// Cached: the picker and the signup city field both ask, and the answer cannot
// change within a page load. A FAILED fetch is not cached, so a blip does not
// leave the map off until the next reload.
let configPromise: Promise<GeoConfig> | null = null;

export const fetchGeoConfig = (): Promise<GeoConfig> => {
  if (configPromise) return configPromise;

  // An async function, so nothing here can throw synchronously into a
  // component's effect — a rejected or malformed answer is simply "no config".
  configPromise = (async (): Promise<GeoConfig> => {
    try {
      const res = await publicApi.get('/geo/config');
      const body = res.data as Partial<GeoConfig> | null;

      return {
        map: body && body.map && body.map.style_url ? body.map : null,
        geocoding: (body && body.geocoding) || null,
      };
    } catch (error) {
      configPromise = null;

      return NO_CONFIG;
    }
  })();

  return configPromise;
};

const placesOf = (data: unknown): GeoPlace[] => {
  const rows = (data as { results?: unknown })?.results;

  return Array.isArray(rows) ? (rows as GeoPlace[]) : [];
};

/**
 * Full addresses, for the building form. Signed-in staff only.
 *
 * It still degrades to an empty list — but `onFailed` is told when the list is
 * empty because the lookup FAILED (the provider slow or down, asked too often)
 * rather than because nothing matched. Without it the form showed the same
 * silence for both, and "ask again" looked like "no such address" (seen on
 * production 2026-10-14: the provider over its time limit, three times in an
 * hour, and the box simply offered nothing).
 */
export const searchAddresses = async (
  q: string,
  near?: { lat: number; lng: number } | null,
  onFailed?: () => void
): Promise<GeoPlace[]> => {
  try {
    const res = await axios.get(`${apiRoute}/geo/autocomplete`, {
      params: near ? { q, lat: near.lat, lng: near.lng } : { q },
    });

    return placesOf(res.data);
  } catch (error) {
    onFailed?.();

    return [];
  }
};

/** City names only, for the signup wizard (no session there). */
export const searchCities = async (q: string): Promise<GeoPlace[]> => {
  try {
    const res = await publicApi.get('/geo/cities', { params: { q } });

    return placesOf(res.data);
  } catch (error) {
    return [];
  }
};

/**
 * THE CITY NEAR A POINT, for the signup form's location ask (WP-B14 / WP-C11).
 *
 * ROUNDED TO TWO DECIMALS HERE, before it leaves the browser — about a
 * kilometre. The server rounds too and the edge's log redacts the parameters,
 * but the exact position should never be on the wire in the first place.
 *
 * Null for everything that is not a city: no city there, a refusal for being
 * asked too often (429), the provider being off or down (503), a lost
 * connection. The caller then offers nothing — the field works without it.
 */
export const cityNear = async (
  latitude: number,
  longitude: number
): Promise<GeoPlace | null> => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  try {
    const res = await publicApi.get('/geo/near', {
      params: { lat: roundCoordinate(latitude), lng: roundCoordinate(longitude) },
    });
    const place = res.data?.result;

    return place && typeof place === 'object' ? (place as GeoPlace) : null;
  } catch (error) {
    return null;
  }
};

/** The address under a pin, or null when there is none or the lookup failed. */
export const reverseGeocode = async (
  lat: number,
  lng: number
): Promise<GeoPlace | null> => {
  try {
    const res = await axios.get(`${apiRoute}/geo/reverse`, { params: { lat, lng } });

    return ((res.data as { result?: GeoPlace | null })?.result as GeoPlace) || null;
  } catch (error) {
    return null;
  }
};

/**
 * The time zone a pin sits in. Answered from data on our own server, so it
 * works with no address provider at all. null = the request itself failed.
 */
export const zoneAtPoint = async (
  lat: number,
  lng: number
): Promise<ZoneAtPoint | null> => {
  try {
    const res = await axios.get(`${apiRoute}/geo/timezone`, { params: { lat, lng } });
    const body = res.data as Partial<ZoneAtPoint>;

    return {
      timezone: typeof body.timezone === 'string' ? body.timezone : null,
      ambiguous: Boolean(body.ambiguous),
      candidates: Array.isArray(body.candidates) ? body.candidates : [],
    };
  } catch (error) {
    return null;
  }
};

// The zones the server will accept — the only list a form may offer. Falls back
// to the browser's own table, which the server may still refuse a name from.
let zonesPromise: Promise<string[]> | null = null;

const browserZones = (): string[] =>
  (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf?.('timeZone') || [];

export const fetchZones = (): Promise<string[]> => {
  if (zonesPromise) return zonesPromise;

  zonesPromise = (async (): Promise<string[]> => {
    try {
      const res = await axios.get(`${apiRoute}/geo/zones`);
      const zones = (res.data as { zones?: unknown })?.zones;

      return Array.isArray(zones) && zones.length ? (zones as string[]) : browserZones();
    } catch (error) {
      zonesPromise = null;

      return browserZones();
    }
  })();

  return zonesPromise;
};

/* ── THE SIGNUP WIZARD'S MAP (signed out) ──────────────────────────────────
   The same four lookups the building forms make, through the public routes
   (/geo/public/*): no session exists yet. The server charges the two that
   reach the address provider to its anonymous daily budget. Each degrades the
   same way its staff twin does. */

export const searchAddressesPublic = async (
  q: string,
  near?: { lat: number; lng: number } | null,
  onFailed?: () => void
): Promise<GeoPlace[]> => {
  try {
    const res = await publicApi.get('/geo/public/autocomplete', {
      params: near ? { q, lat: near.lat, lng: near.lng } : { q },
    });

    return placesOf(res.data);
  } catch (error) {
    onFailed?.();

    return [];
  }
};

export const reverseGeocodePublic = async (
  lat: number,
  lng: number
): Promise<GeoPlace | null> => {
  try {
    const res = await publicApi.get('/geo/public/reverse', { params: { lat, lng } });

    return ((res.data as { result?: GeoPlace | null })?.result as GeoPlace) || null;
  } catch (error) {
    return null;
  }
};

export const zoneAtPointPublic = async (
  lat: number,
  lng: number
): Promise<ZoneAtPoint | null> => {
  try {
    const res = await publicApi.get('/geo/public/timezone', { params: { lat, lng } });
    const body = res.data as Partial<ZoneAtPoint>;

    return {
      timezone: typeof body.timezone === 'string' ? body.timezone : null,
      ambiguous: Boolean(body.ambiguous),
      candidates: Array.isArray(body.candidates) ? body.candidates : [],
    };
  } catch (error) {
    return null;
  }
};

let publicZonesPromise: Promise<string[]> | null = null;

export const fetchZonesPublic = (): Promise<string[]> => {
  if (publicZonesPromise) return publicZonesPromise;

  publicZonesPromise = (async (): Promise<string[]> => {
    try {
      const res = await publicApi.get('/geo/public/zones');
      const zones = (res.data as { zones?: unknown })?.zones;

      return Array.isArray(zones) && zones.length ? (zones as string[]) : browserZones();
    } catch (error) {
      publicZonesPromise = null;

      return browserZones();
    }
  })();

  return publicZonesPromise;
};

/* ── THE MAP AS THIS BROWSER SAW IT ────────────────────────────────────────
   The tiles come straight from the tile host to the browser, so a failure here
   is invisible to the server. ONE report per page load reaches the Super Admin
   console's Map page (POST /geo/map-failure): the kind of failure, the HTTP
   status and MapLibre's own message — never where the map was looking. Fire
   and forget: a report that cannot be sent changes nothing on screen. */
let mapFailureSent = false;
// Leaving the page cancels the map's tile loads, and the map reports each one
// as an error. Those are not failures, and a request sent while the page is
// going away is itself refused (WebKit says so as an "access control" error).
let pageLeaving = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageLeaving = true;
  });
  window.addEventListener('beforeunload', () => {
    pageLeaving = true;
  });
  // Back from the back-forward cache: the page is live again.
  window.addEventListener('pageshow', () => {
    pageLeaving = false;
  });
}

export const reportMapFailure = (failure: {
  kind: 'style' | 'tile' | 'source' | 'webgl' | 'other';
  status?: number | null;
  message?: string | null;
  url?: string | null;
}): void => {
  if (mapFailureSent || pageLeaving) return;
  // A load the map itself cancelled (it moved on, or was taken down) is not a
  // failure of the tile host.
  if (/abort|cancel/i.test(String(failure.message || ''))) return;
  mapFailureSent = true;
  let host: string | null = null;
  try {
    host = failure.url ? new URL(failure.url).host : null;
  } catch {
    host = null;
  }
  publicApi
    .post('/geo/map-failure', {
      kind: failure.kind,
      status: failure.status ?? null,
      // A tile URL's path is z/x/y — WHERE the map was looking. Only its host
      // is kept.
      message: failure.message
        ? String(failure.message)
            .replace(/(https?:\/\/[^/\s]+)[^\s]*/g, '$1/…')
            .slice(0, 300)
        : null,
      host,
    })
    .catch(() => {
      /* nothing to do: the map already shows its own fallback */
    });
};
